import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, of, switchMap, take } from 'rxjs';
import { LucideAngularModule, CheckCircle2, X } from 'lucide-angular';
import { AssignmentService } from './services/assignment.service';
import { ASSIGNMENT_THEME_COLOR } from './services/assignment.models';
import { AmplitudeService } from '../../../common/analytics/amplitude.service';
import { AuthService } from '../../../common/auth/auth.service';
import { ConfirmDialogComponent } from '../../../common/components/confirm-dialog/confirm-dialog';
import { StepContextComponent } from './steps/step-context/step-context';
import { StepObjectiveComponent } from './steps/step-objective/step-objective';
import { StepQuestionsComponent } from './steps/step-questions/step-questions';
import { StepSettingsComponent } from './steps/step-settings/step-settings';
import { StepReadyComponent } from './steps/step-ready/step-ready';

interface WizardStep {
  num: number;
  title: string;
}

@Component({
  selector: 'app-assignment',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ConfirmDialogComponent,
    StepContextComponent,
    StepObjectiveComponent,
    StepQuestionsComponent,
    StepSettingsComponent,
    StepReadyComponent,
  ],
  templateUrl: './assignment.html',
  styleUrl: './assignment.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly assignmentService = inject(AssignmentService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('exitDialog') exitDialog!: ConfirmDialogComponent;

  protected readonly themeColor = ASSIGNMENT_THEME_COLOR;
  protected readonly currentStep$ = this.assignmentService.currentStep$;
  protected readonly isLoading$ = this.assignmentService.isLoading$;
  protected readonly error$ = this.assignmentService.error$;
  protected readonly isGenerating$ = this.assignmentService.isGenerating$;
  protected readonly isAuthenticated$ = this.authService.isAuthenticated();

  protected readonly icons = {
    CheckCircle2,
    X,
  };

  // Steps shown in stepper (Configure, Objective, Questions, Settings)
  // Ready is a separate page without stepper
  protected readonly steps: WizardStep[] = [
    { num: 1, title: 'Configure' },
    { num: 2, title: 'Objective' },
    { num: 3, title: 'Questions' },
    { num: 4, title: 'Settings' },
  ];

  // Check if stepper should be shown (steps 1-4, not Ready)
  protected readonly showStepper$ = this.currentStep$.pipe(
    map((step) => step >= 1 && step <= 4),
  );

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const id = params.get('id');
        const queryParams = this.route.snapshot.queryParams;
        const shouldPublish = queryParams['publish'] === 'true';

        if (id) {
          // Check if we already have this assignment loaded in memory
          if (this.assignmentService.assignmentId === id && !shouldPublish) {
            return;
          }

          // Load existing assignment from backend
          this.assignmentService
            .loadAssignment(id)
            .pipe(
              take(1),
              switchMap((assignment) => {
                if (!assignment) {
                  // Assignment not found, redirect to create new
                  this.router.navigate(['/assignment']);
                  return of(null);
                }

                // If publish flag is set (coming from auth redirect), assign and publish
                if (shouldPublish) {
                  return this.handlePostAuthPublish();
                }

                return of(assignment);
              })
            )
            .subscribe();
        } else {
          // Creating new assignment - reset state
          // Query params (grade, tier1SubjectId) are read by StepContextComponent
          this.assignmentService.reset();
        }
      });
  }

  /**
   * Handle post-auth publish flow.
   * Assigns the assignment to the current user and then publishes it.
   */
  private handlePostAuthPublish() {
    // Clear the query param to avoid re-triggering
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });

    // First assign the assignment to the current user
    return this.assignmentService.assignToUser().pipe(
      catchError(() => {
        // If assign fails (e.g., already owned), continue to publish
        return of(undefined);
      }),
      switchMap(() => {
        // Then publish the assignment
        return this.assignmentService.publishAssignment().pipe(
          catchError(() => of(undefined))
        );
      }),
      map(() => {
        const inputData = this.assignmentService.inputData;
        this.amplitudeService.trackEvent('assignment_published', {
          assignment_id: this.assignmentService.assignmentId,
          grade: inputData.grade,
          subject: inputData.tier2SubjectId,
          total_questions: this.assignmentService.questions.length,
        });

        // Navigate to ready step
        this.assignmentService.setStep(5);
        return null;
      })
    );
  }

  protected onExitWizard(): void {
    this.exitDialog.isOpen = true;
  }

  protected onExitConfirmed(): void {
    this.router.navigate(['/teacher/dashboard/assignments']);
  }

  protected getProgressWidth(currentStep: number): string {
    const totalSteps = this.steps.length;
    const segments = currentStep - 1;
    if (segments === 0) return '0';

    // Each segment = (100% - 2.5rem) / (totalSteps - 1)
    return `calc(${segments} * (100% - 2.5rem) / ${totalSteps - 1} - 0.5rem)`;
  }

  protected getMobileProgressWidth(currentStep: number): string {
    const totalSteps = this.steps.length;
    return `${(currentStep / totalSteps) * 100}%`;
  }

  protected getStepTitle(currentStep: number): string {
    return this.steps[currentStep - 1]?.title || '';
  }

  protected onStepNext(): void {
    this.assignmentService.nextStep();
    this.scrollToTop();
  }

  protected onStepBack(): void {
    this.assignmentService.prevStep();
    this.scrollToTop();
  }

  /**
   * Scroll to top with Safari mobile repaint fix
   */
  private scrollToTop(): void {
    const isSafariMobile =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes('Safari') && 'ontouchstart' in window);

    window.scrollTo({
      top: 0,
      behavior: isSafariMobile ? 'instant' : 'smooth',
    });

    if (isSafariMobile) {
      requestAnimationFrame(() => {
        document.body.style.transform = 'translateZ(0)';
        requestAnimationFrame(() => {
          document.body.style.transform = '';
        });
      });
    }
  }
}
